import React from "react";
import {
    Grid,
    TextField,
    Typography,
    Tooltip,
    Box,
    Stack,
    CircularProgress,
    Link,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    type SelectChangeEvent,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import ActionStepsInput from "../../components/ActionStepsInput";
import {
    creatorSourceLanguageSelectLabels,
    eventLanguageSortRank,
    normalizeEventLanguage,
    supportedEventLanguages,
    type EventLanguageCode,
} from "../../constants/languages";
import { ensureCvcLabelLongEndsWithDot } from "../../shared/cvcLabelLong";
import { config } from "../../config";
import { useEventsApi } from "../../hooks/useEventsApi";
import { useExportCodePreference } from "../../context/ExportCodePreferenceContext";
import { WordingGuardSuggestionChip } from "../../components/WordingGuardSuggestionChip";

export interface EventDescriptionsProps {
    descriptions: any[];
    errors: Record<string, string>;
    isReader: boolean;
    isManufacturer: boolean;
    eventCode: number;
    severity: string;
    isFallingEvent?: boolean;
    onChange: (eventOrActions: any, lang: string) => void;
    onRequestSimilarEvents?: (query: string, lang: string) => Promise<SimilarEventHint[]>;
    onOpenSimilarEvent?: (eventId: number) => void;
    configuratorMode?: boolean;
    translationStatus?: {
        status?: string;
        message?: string;
        translated_languages?: string[];
    };
    /** Normalized ger | eng | ita | cze — which language block is shown and edited in creator mode */
    creatorPrimaryLanguage?: EventLanguageCode;
    onCreatorPrimaryLanguageChange?: (lang: EventLanguageCode) => void;
    /** Resolving event form: HMI text of the linked rising event per language (hint only). */
    resolvingRisingHmiHintByLang?: Record<string, string>;
}

export interface SimilarEventHint {
    global_event_id: number;
    event_code: number;
    event_type: string;
    event_contract_name: string;
    label: string;
    language_code: string;
    similarity: number;
    detail_path?: string;
}

type DescriptionLocalFields = {
    label: string;
    label_long: string;
    description: string;
    situation: string;
};

type WordingReplacementRule = {
    source_words: string[];
    target_word: string;
    language_code: string;
    is_active: boolean;
};

type ActionTextSuggestion = {
    id: number;
    text: string;
    language_code: string;
    is_active: boolean;
    sort_order: number;
};

const WORD_BOUNDARY_CLASS = "[^\\p{L}\\p{N}_]";

const escapeRegex = (value: string): string =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

type WordingSuggestion = {
    source: string;
    target: string;
    matches: number;
};

const countMatches = (text: string, source: string): number => {
    if (!text || !source) return 0;
    const pattern = new RegExp(`(^|${WORD_BOUNDARY_CLASS})(${escapeRegex(source)})(?=$|${WORD_BOUNDARY_CLASS})`, "giu");
    let count = 0;
    text.replace(pattern, () => {
        count += 1;
        return "";
    });
    return count;
};

const replaceOneSuggestion = (text: string, source: string, target: string): string => {
    const pattern = new RegExp(`(^|${WORD_BOUNDARY_CLASS})(${escapeRegex(source)})(?=$|${WORD_BOUNDARY_CLASS})`, "giu");
    return text.replace(pattern, (_match, prefix) => `${prefix}${target}`);
};

const collectSuggestions = (text: string, rules: WordingReplacementRule[]): WordingSuggestion[] => {
    if (!text || rules.length === 0) return [];
    const out: WordingSuggestion[] = [];
    const seen = new Set<string>();
    for (const rule of rules) {
        const target = (rule.target_word ?? "").trim();
        if (!target) continue;
        const sources = (rule.source_words ?? [])
            .map((entry) => String(entry).trim())
            .filter((entry) => entry.length > 0);
        for (const source of sources) {
            if (source.toLowerCase() === target.toLowerCase()) continue;
            const key = `${source.toLowerCase()}->${target.toLowerCase()}`;
            if (seen.has(key)) continue;
            const matches = countMatches(text, source);
            if (matches > 0) {
                out.push({ source, target, matches });
                seen.add(key);
            }
        }
    }
    return out;
};

interface BufferedTextFieldProps {
    label: string;
    value: string;
    readOnly: boolean;
    error: boolean;
    helperText: string;
    multiline?: boolean;
    rows?: number;
    sx?: any;
    maxLength?: number;
    showCharacterCount?: boolean;
    onValueChange?: (value: string) => void;
    onCommit: (value: string) => void;
}

const BufferedTextField = React.memo((props: BufferedTextFieldProps) => {
    const {
        label,
        value,
        readOnly,
        error,
        helperText,
        multiline,
        rows,
        sx,
        maxLength,
        showCharacterCount,
        onValueChange,
        onCommit,
    } = props;
    const [localValue, setLocalValue] = React.useState(value ?? "");
    const commitRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        if (value !== localValue) {
            setLocalValue(value ?? "");
        }
    }, [value]);

    React.useEffect(() => {
        return () => {
            if (commitRef.current) {
                window.clearTimeout(commitRef.current);
            }
        };
    }, []);

    return (
        <TextField
            label={label}
            value={localValue}
            multiline={multiline}
            rows={rows}
            onChange={(e) => {
                const next = maxLength ? e.target.value.slice(0, maxLength) : e.target.value;
                setLocalValue(next);
                onValueChange?.(next);
                if (commitRef.current) {
                    window.clearTimeout(commitRef.current);
                }
                commitRef.current = window.setTimeout(() => {
                    onCommit(next);
                    commitRef.current = null;
                }, 220);
            }}
            fullWidth
            slotProps={{
                input: {
                    readOnly,
                    inputProps: maxLength ? { maxLength } : undefined,
                },
                inputLabel: { shrink: true },
            }}
            error={error}
            helperText={
                showCharacterCount && maxLength
                    ? (
                        <>
                            <Box component="span">{helperText}</Box>
                            <Box component="span" sx={{ display: "inline-block", ml: 3 }}>
                                {`${localValue.length}/${maxLength}`}
                            </Box>
                        </>
                    )
                    : helperText
            }
            sx={sx}
        />
    );
});

export const EventDescriptions: React.FC<EventDescriptionsProps> = React.memo(
    ({ descriptions, errors, isReader, isManufacturer, eventCode, severity, isFallingEvent, onChange, onRequestSimilarEvents, onOpenSimilarEvent, configuratorMode, translationStatus, creatorPrimaryLanguage = "ger", onCreatorPrimaryLanguageChange, resolvingRisingHmiHintByLang }) => {
        const { callApi } = useEventsApi();
        const { wordingGuardEnabled, textSuggestionsEnabled } = useExportCodePreference();
        const sorted = React.useMemo(() => {
            return [...descriptions].sort((a, b) => {
                const rankDiff = eventLanguageSortRank(a.language_code) - eventLanguageSortRank(b.language_code);
                if (rankDiff !== 0) return rankDiff;
                return a.language_code.localeCompare(b.language_code);
            });
        }, [descriptions]);

        const primaryLang = normalizeEventLanguage(creatorPrimaryLanguage) as EventLanguageCode;

        const visibleDescriptions = React.useMemo(() => {
            if (!configuratorMode) {
                return sorted;
            }
            return sorted.filter(
                (d) => normalizeEventLanguage(d.language_code) === primaryLang
            );
        }, [configuratorMode, sorted, primaryLang]);

        const requiresText = !isFallingEvent;
        const PREFIXES_HMI_EVENT_TEXT: Record<string, Record<string, string>> = {
            ger: {
                "Info": "Info: ",
                "Warning": "Warnung: ",
                "Error": "Not-Halt: ",
                "Fatal": "Not-Halt: ",
            },
            eng: {
                "Info": "Info: ",
                "Warning": "Warning: ",
                "Error": "E-Stop: ",
                "Fatal": "E-Stop: ",
            },
            ita: {
                "Info": "Info: ",
                "Warning": "Avviso: ",
                "Error": "E-Stop: ",
                "Fatal": "E-Stop: ",
            },
            cze: {
                "Info": "Info: ",
                "Warning": "Varovani: ",
                "Error": "E-Stop: ",
                "Fatal": "E-Stop: ",
            },
        };
        const PREFIXES_CVC_EVENT_TEXT: Record<string, Record<string, string>> = {
            ger: {
                "Info": "FTF {6:d} Info auf Segment {1:d}: ",
                "Warning": "FTF {6:d} Warnung auf Segment {1:d}: ",
                "Error": "FTF {6:d} Not-Halt auf Segment {1:d}: ",
                "Fatal": "FTF {6:d} Not-Halt auf Segment {1:d}: ",
            },
            eng: {
                "Info": "AGV {6:d} info on segment {1:d}: ",
                "Warning": "AGV {6:d} warning on segment {1:d}: ",
                "Error": "AGV {6:d} E-Stop on segment {1:d}: ",
                "Fatal": "AGV {6:d} E-Stop on segment {1:d}: ",
            },
            ita: {
                "Info": "AGV {6:d} info sul segmento {1:d}: ",
                "Warning": "AGV {6:d} avviso sul segmento {1:d}: ",
                "Error": "AGV {6:d} Stop di emergenza sul segmento {1:d}: ",
                "Fatal": "AGV {6:d} Stop di emergenza sul segmento {1:d}: ",
            },
            cze: {
                "Info": "AGV {6:d} info na segmentu {1:d}: ",
                "Warning": "AGV {6:d} varovani na segmentu {1:d}: ",
                "Error": "AGV {6:d} Nouzové zastavení na segmentu {1:d}: ",
                "Fatal": "AGV {6:d} Nouzové zastavení na segmentu {1:d}: ",
            },
        };

        const [similarByLang, setSimilarByLang] = React.useState<Record<string, SimilarEventHint[]>>({});
        const [loadingByLang, setLoadingByLang] = React.useState<Record<string, boolean>>({});
        const [wordingRules, setWordingRules] = React.useState<WordingReplacementRule[]>([]);
        const [wordingRulesLoaded, setWordingRulesLoaded] = React.useState(false);
        const [actionTemplates, setActionTemplates] = React.useState<string[]>([]);
        const [actionTemplatesLoaded, setActionTemplatesLoaded] = React.useState(false);
        const [draftByField, setDraftByField] = React.useState<Record<string, string>>({});
        const [dismissedByField, setDismissedByField] = React.useState<Record<string, Set<string>>>({});
        const [, startTransition] = React.useTransition();
        const debounceHandleRef = React.useRef<Record<string, number>>({});
        const requestSeqRef = React.useRef<Record<string, number>>({});

        const triggerSimilarLookup = React.useCallback((query: string, lang: string) => {
            if (!onRequestSimilarEvents) {
                return;
            }

            const normalizedLang = normalizeEventLanguage(lang);
            const trimmed = (query ?? "").trim();

            if (debounceHandleRef.current[normalizedLang]) {
                window.clearTimeout(debounceHandleRef.current[normalizedLang]);
            }

            // Keep UI typing smooth: search only after meaningful input length.
            if (trimmed.length < 6) {
                startTransition(() => {
                    setSimilarByLang(prev => ({ ...prev, [normalizedLang]: [] }));
                    setLoadingByLang(prev => ({ ...prev, [normalizedLang]: false }));
                });
                return;
            }

            debounceHandleRef.current[normalizedLang] = window.setTimeout(async () => {
                const nextSeq = (requestSeqRef.current[normalizedLang] ?? 0) + 1;
                requestSeqRef.current[normalizedLang] = nextSeq;
                startTransition(() => {
                    setLoadingByLang(prev => ({ ...prev, [normalizedLang]: true }));
                });
                try {
                    const result = await onRequestSimilarEvents(trimmed, normalizedLang);
                    if (requestSeqRef.current[normalizedLang] === nextSeq) {
                        startTransition(() => {
                            setSimilarByLang(prev => ({ ...prev, [normalizedLang]: result ?? [] }));
                        });
                    }
                } catch (error) {
                    if (requestSeqRef.current[normalizedLang] === nextSeq) {
                        startTransition(() => {
                            setSimilarByLang(prev => ({ ...prev, [normalizedLang]: [] }));
                        });
                    }
                } finally {
                    if (requestSeqRef.current[normalizedLang] === nextSeq) {
                        startTransition(() => {
                            setLoadingByLang(prev => ({ ...prev, [normalizedLang]: false }));
                        });
                    }
                }
            }, 450);
        }, [onRequestSimilarEvents]);

        React.useEffect(() => {
            const activeDebounceHandles = debounceHandleRef.current;
            return () => {
                Object.values(activeDebounceHandles).forEach((handle) => window.clearTimeout(handle));
            };
        }, []);

        React.useEffect(() => {
            if (!wordingGuardEnabled || wordingRulesLoaded) {
                return;
            }
            let cancelled = false;
            (async () => {
                try {
                    const response = await callApi(
                        `${config.apiUrl}/api/wording-replacements/?active=true`,
                        "GET"
                    );
                    const rows = Array.isArray(response) ? response : response?.results;
                    if (!cancelled && Array.isArray(rows)) {
                        setWordingRules(rows as WordingReplacementRule[]);
                    }
                } catch {
                    if (!cancelled) {
                        setWordingRules([]);
                    }
                } finally {
                    if (!cancelled) {
                        setWordingRulesLoaded(true);
                    }
                }
            })();
            return () => {
                cancelled = true;
            };
        }, [callApi, wordingRulesLoaded, wordingGuardEnabled]);

        const wordingRulesByLang = React.useMemo(() => {
            const map: Record<string, WordingReplacementRule[]> = {};
            for (const rule of wordingRules) {
                const key = normalizeEventLanguage(rule.language_code);
                if (!map[key]) map[key] = [];
                map[key].push(rule);
            }
            return map;
        }, [wordingRules]);

        React.useEffect(() => {
            if (!textSuggestionsEnabled || actionTemplatesLoaded) {
                return;
            }
            let cancelled = false;
            (async () => {
                try {
                    const response = await callApi(
                        `${config.apiUrl}/api/action-text-suggestions/?language_code=ger&active=true`,
                        "GET"
                    );
                    const rows = Array.isArray(response) ? response : response?.results;
                    if (!cancelled && Array.isArray(rows)) {
                        const next = (rows as ActionTextSuggestion[])
                            .map((row) => String(row.text || "").trim())
                            .filter((value) => value.length > 0);
                        setActionTemplates(next);
                    }
                } catch {
                    if (!cancelled) {
                        setActionTemplates([]);
                    }
                } finally {
                    if (!cancelled) {
                        setActionTemplatesLoaded(true);
                    }
                }
            })();
            return () => {
                cancelled = true;
            };
        }, [actionTemplatesLoaded, callApi, textSuggestionsEnabled]);

        React.useEffect(() => {
            if (textSuggestionsEnabled) return;
            setActionTemplates([]);
            setActionTemplatesLoaded(false);
        }, [textSuggestionsEnabled]);

        const getEffectiveFieldValue = React.useCallback((fieldKey: string, committed: string): string => {
            return draftByField[fieldKey] ?? committed ?? "";
        }, [draftByField]);
        const infoIconGridSxSingle = {
            display: "flex",
            alignItems: "flex-start",
            pt: 2.25,
            color: "primary.main",
        } as const;
        const infoIconGridSxMultiline = {
            display: "flex",
            alignItems: "flex-start",
            pt: 3.75,
            color: "primary.main",
        } as const;

        const getSuggestionsForField = React.useCallback(
            (fieldKey: string, text: string, lang: string): WordingSuggestion[] => {
                if (!wordingGuardEnabled) {
                    return [];
                }
                const normalizedLang = normalizeEventLanguage(lang);
                const rulesForLang = wordingRulesByLang[normalizedLang] ?? [];
                if (rulesForLang.length === 0) {
                    return [];
                }
                const dismissed = dismissedByField[fieldKey] ?? new Set<string>();
                return collectSuggestions(text, rulesForLang).filter(
                    (s) => !dismissed.has(`${s.source.toLowerCase()}->${s.target.toLowerCase()}`)
                );
            },
            [wordingGuardEnabled, wordingRulesByLang, dismissedByField]
        );

        const dismissSuggestion = React.useCallback((fieldKey: string, source: string, target: string) => {
            const token = `${source.toLowerCase()}->${target.toLowerCase()}`;
            setDismissedByField((prev) => {
                const next = { ...prev };
                const set = new Set(next[fieldKey] ?? []);
                set.add(token);
                next[fieldKey] = set;
                return next;
            });
        }, []);

        const acceptSuggestion = React.useCallback((
            fieldKey: string,
            lang: string,
            name: "label" | "label_long" | "description" | "situation",
            currentValue: string,
            source: string,
            target: string,
            severityForLabel: string
        ) => {
            const replaced = replaceOneSuggestion(currentValue, source, target);
            setDraftByField((prev) => ({ ...prev, [fieldKey]: replaced }));
            onChange({ target: { name, value: replaced } }, lang);

            if (name === "label") {
                onChange(
                    {
                        target: {
                            name: "label_long",
                            value: ensureCvcLabelLongEndsWithDot(
                                `${PREFIXES_CVC_EVENT_TEXT[lang]?.[severityForLabel] ?? ""}${replaced}`
                            ),
                        },
                    },
                    lang
                );
            }
        }, [onChange, PREFIXES_CVC_EVENT_TEXT]);

        return (
            <Grid container spacing={2}>
                {configuratorMode && (
                    <Grid size={{ xs: 12 }}>
                        <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: "action.hover", mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                Creator mode: enter text in one language only. Other languages are generated automatically
                                and can be edited later in the event dialog.
                            </Typography>
                            {onCreatorPrimaryLanguageChange && (
                                <FormControl size="small" sx={{ minWidth: 220, mb: 1 }}>
                                    <InputLabel id="creator-input-language-label">Input language</InputLabel>
                                    <Select<EventLanguageCode>
                                        labelId="creator-input-language-label"
                                        label="Input language"
                                        value={primaryLang}
                                        onChange={(e: SelectChangeEvent<EventLanguageCode>) =>
                                            onCreatorPrimaryLanguageChange(e.target.value as EventLanguageCode)
                                        }
                                    >
                                        {supportedEventLanguages.map((code) => (
                                            <MenuItem key={code} value={code}>
                                                {creatorSourceLanguageSelectLabels[code]}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {translationStatus?.status && translationStatus.status !== "idle" && (
                                <Typography variant="caption" display="block">
                                    Translation status: {translationStatus.status}
                                    {translationStatus?.translated_languages?.length
                                        ? ` (${translationStatus.translated_languages.join(", ")})`
                                        : ""}
                                    {translationStatus?.message ? ` - ${translationStatus.message}` : ""}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                )}
                {visibleDescriptions.map((desc) => {
                    const lang = normalizeEventLanguage(desc.language_code);
                    const similarEvents = similarByLang[lang] ?? [];
                    const loadingSimilar = loadingByLang[lang] ?? false;
                    const effectiveReadOnly = isReader || isManufacturer;
                    const labelKey = `${lang}.label`;
                    const labelLongKey = `${lang}.label_long`;
                    const descriptionKey = `${lang}.description`;
                    const situationKey = `${lang}.situation`;

                    const labelValue = getEffectiveFieldValue(labelKey, desc.label ?? "");
                    const labelLongValue = getEffectiveFieldValue(labelLongKey, desc.label_long ?? "");
                    const descriptionValue = getEffectiveFieldValue(descriptionKey, desc.description ?? "");
                    const situationValue = getEffectiveFieldValue(situationKey, desc.situation ?? "");

                    const labelSuggestions = getSuggestionsForField(labelKey, labelValue, lang);
                    const labelLongSuggestions = getSuggestionsForField(labelLongKey, labelLongValue, lang);
                    const descriptionSuggestions = getSuggestionsForField(descriptionKey, descriptionValue, lang);
                    const situationSuggestions = getSuggestionsForField(situationKey, situationValue, lang);
                    const getActionFieldKey = (index: number) => `${lang}.actions.${index}`;
                    const getActionSuggestions = (actionText: string, index: number) =>
                        getSuggestionsForField(getActionFieldKey(index), actionText, lang);
                    const acceptActionSuggestion = (index: number, source: string, target: string) => {
                        const currentActions = Array.isArray(desc.actions) ? [...desc.actions] : [];
                        const current = currentActions[index] ?? "";
                        const replaced = replaceOneSuggestion(current, source, target);
                        currentActions[index] = replaced;
                        const fieldKey = getActionFieldKey(index);
                        setDraftByField((prev) => ({ ...prev, [fieldKey]: replaced }));
                        onChange(currentActions, lang);
                    };
                    const ignoreActionSuggestion = (index: number, source: string, target: string) => {
                        dismissSuggestion(getActionFieldKey(index), source, target);
                    };

                    return (
                        <React.Fragment key={lang}>
                            <Grid size={{ xs: 12 }}>
                                <Typography sx={{ mb: 2 }}>
                                    Language "{lang}"
                                </Typography>
                            </Grid>

                            {/* LABEL */}
                            <Grid size={{ xs: 11 }}>
                                {isFallingEvent && resolvingRisingHmiHintByLang?.[lang] ? (
                                    <Alert
                                        severity="info"
                                        variant="outlined"
                                        sx={(theme) => ({
                                            mb: 1.5,
                                            borderWidth: 2,
                                            borderColor: theme.palette.info.main,
                                            backgroundColor:
                                                theme.palette.mode === "dark"
                                                    ? "rgba(33, 150, 243, 0.12)"
                                                    : "rgba(33, 150, 243, 0.08)",
                                            "& .MuiAlert-icon": {
                                                alignItems: "center",
                                                fontSize: 28,
                                            },
                                        })}
                                    >
                                        <Typography variant="subtitle2" component="div" fontWeight={700} sx={{ mb: 0.5 }}>
                                            Rising event — HMI reference ({lang.toUpperCase()})
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.5 }}>
                                            {resolvingRisingHmiHintByLang[lang]}
                                        </Typography>
                                    </Alert>
                                ) : null}
                                <BufferedTextField
                                    label={`HMI Event-Text (${lang})`}
                                    value={desc.label ?? ""}
                                    maxLength={config.hmiEventTextMaxLength}
                                    showCharacterCount
                                    readOnly={effectiveReadOnly}
                                    error={Boolean(errors[`desc.${lang}.label`])}
                                    helperText={errors[`desc.${lang}.label`]
                                        ?? "Preview: " + (PREFIXES_HMI_EVENT_TEXT[lang]?.[severity] ?? " ") + (desc.label ?? "")}
                                    onValueChange={(next) => triggerSimilarLookup(next, lang)}
                                    onCommit={(next) => {
                                        setDraftByField((prev) => ({ ...prev, [labelKey]: next }));
                                        setDismissedByField((prev) => ({ ...prev, [labelKey]: new Set<string>() }));
                                        onChange({ target: { name: "label", value: next } }, lang);
                                        onChange(
                                            {
                                                target: {
                                                    name: "label_long",
                                                    value: ensureCvcLabelLongEndsWithDot(
                                                        `${PREFIXES_CVC_EVENT_TEXT[lang]?.[severity] ?? ""}${next}`
                                                    ),
                                                },
                                            },
                                            lang
                                        );
                                    }}
                                    sx={{
                                        ...(labelSuggestions.length > 0
                                            ? {
                                                "& .MuiOutlinedInput-root": {
                                                    backgroundColor: (theme: Theme) => alpha(theme.palette.warning.main, 0.08),
                                                    "& fieldset": { borderColor: "warning.main" },
                                                },
                                            }
                                            : {}),
                                    }}
                                />
                                {labelSuggestions.length > 0 && !effectiveReadOnly && (
                                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                                        {labelSuggestions.map((s) => (
                                            <WordingGuardSuggestionChip
                                                key={`${labelKey}-${s.source}-${s.target}`}
                                                label={`${s.source} -> ${s.target} (${s.matches})`}
                                                onAccept={() =>
                                                    acceptSuggestion(labelKey, lang, "label", labelValue, s.source, s.target, severity)
                                                }
                                                onDismiss={() => dismissSuggestion(labelKey, s.source, s.target)}
                                            />
                                        ))}
                                    </Stack>
                                )}
                                {!isReader && !isManufacturer && onRequestSimilarEvents && (
                                    <Box sx={{ mt: 1 }}>
                                        {loadingSimilar && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <CircularProgress size={14} />
                                                <Typography variant="caption">Searching similar events...</Typography>
                                            </Box>
                                        )}
                                        {!loadingSimilar && similarEvents.length > 0 && (
                                            <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: "action.hover" }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                    Similar events found
                                                </Typography>
                                                {similarEvents.map((item) => (
                                                    <Box key={`${lang}-${item.global_event_id}`} sx={{ mt: 0.5 }}>
                                                        <Link
                                                            component="button"
                                                            type="button"
                                                            onClick={() => onOpenSimilarEvent?.(item.global_event_id)}
                                                            underline="hover"
                                                        >
                                                            #{item.event_code} ({item.event_type}) - {item.label} [{Math.round(item.similarity * 100)}%]
                                                        </Link>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                            <Grid size={{ xs: 1 }} sx={infoIconGridSxSingle}>
                                <Tooltip title="Event text shown on HMI" arrow>
                                    <span style={{ display: "inline-flex" }}>
                                        <InfoOutlineIcon />
                                    </span>
                                </Tooltip>
                            </Grid>

                            {/* LABEL_LONG */}
                            <Grid size={{ xs: 11 }}>
                                <BufferedTextField
                                    label={`CVC Event-Text (${lang})`}
                                    value={desc.label_long ?? ""}
                                    readOnly={effectiveReadOnly}
                                    error={Boolean(errors[`desc.${lang}.label_long`])}
                                    helperText={errors[`desc.${lang}.label_long`] ?? "A suggestion is automatically generated when the HMI Event-Text is changed."}
                                    multiline
                                    rows={2}
                                    onCommit={(next) =>
                                        (setDraftByField((prev) => ({ ...prev, [labelLongKey]: next })),
                                        setDismissedByField((prev) => ({ ...prev, [labelLongKey]: new Set<string>() })),
                                        onChange(
                                            {
                                                target: {
                                                    name: "label_long",
                                                    value: ensureCvcLabelLongEndsWithDot(
                                                        next
                                                    ),
                                                },
                                            },
                                            lang
                                        ))
                                    }
                                    sx={{ '& .MuiInputBase-input': { height: '60px !important' } }}
                                />
                                {labelLongSuggestions.length > 0 && !effectiveReadOnly && (
                                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                                        {labelLongSuggestions.map((s) => (
                                            <WordingGuardSuggestionChip
                                                key={`${labelLongKey}-${s.source}-${s.target}`}
                                                label={`${s.source} -> ${s.target} (${s.matches})`}
                                                onAccept={() =>
                                                    acceptSuggestion(labelLongKey, lang, "label_long", labelLongValue, s.source, s.target, severity)
                                                }
                                                onDismiss={() => dismissSuggestion(labelLongKey, s.source, s.target)}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Grid>
                            <Grid size={{ xs: 1 }} sx={infoIconGridSxMultiline}>
                                <Tooltip title="Event text for Web/CVC/Blackbox" arrow>
                                    <span style={{ display: "inline-flex" }}>
                                        <InfoOutlineIcon />
                                    </span>
                                </Tooltip>
                            </Grid>

                            {/* DESCRIPTION */}
                            <Grid size={{ xs: 11 }}>
                                <BufferedTextField
                                    label={`Description (${lang})`}
                                    value={desc.description ?? ""}
                                    readOnly={effectiveReadOnly}
                                    error={Boolean(errors[`desc.${lang}.description`])}
                                    helperText={errors[`desc.${lang}.description`] ?? " "}
                                    multiline
                                    rows={2}
                                    onCommit={(next) => {
                                        setDraftByField((prev) => ({ ...prev, [descriptionKey]: next }));
                                        setDismissedByField((prev) => ({ ...prev, [descriptionKey]: new Set<string>() }));
                                        onChange({
                                            target: {
                                                name: "description",
                                                value: next,
                                            }
                                        }, lang);
                                    }}
                                    sx={{ '& .MuiInputBase-input': { height: '60px !important' } }}
                                />
                                {descriptionSuggestions.length > 0 && !effectiveReadOnly && (
                                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                                        {descriptionSuggestions.map((s) => (
                                            <WordingGuardSuggestionChip
                                                key={`${descriptionKey}-${s.source}-${s.target}`}
                                                label={`${s.source} -> ${s.target} (${s.matches})`}
                                                onAccept={() =>
                                                    acceptSuggestion(descriptionKey, lang, "description", descriptionValue, s.source, s.target, severity)
                                                }
                                                onDismiss={() => dismissSuggestion(descriptionKey, s.source, s.target)}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Grid>
                            <Grid size={{ xs: 1 }} sx={infoIconGridSxMultiline}>
                                <Tooltip title="Explains context & system behavior" arrow>
                                    <span style={{ display: "inline-flex" }}>
                                        <InfoOutlineIcon />
                                    </span>
                                </Tooltip>
                            </Grid>

                            {/* SITUATION */}
                            <Grid size={{ xs: 11 }}>
                                <BufferedTextField
                                    label={`Situation (${lang})`}
                                    value={desc.situation ?? ""}
                                    readOnly={effectiveReadOnly}
                                    error={Boolean(errors[`desc.${lang}.situation`])}
                                    helperText={errors[`desc.${lang}.situation`] ?? " "}
                                    multiline
                                    rows={2}
                                    onCommit={(next) => {
                                        setDraftByField((prev) => ({ ...prev, [situationKey]: next }));
                                        setDismissedByField((prev) => ({ ...prev, [situationKey]: new Set<string>() }));
                                        onChange({
                                            target: {
                                                name: "situation",
                                                value: next,
                                            }
                                        }, lang);
                                    }}
                                    sx={{ '& .MuiInputBase-input': { height: '60px !important' } }}
                                />
                                {situationSuggestions.length > 0 && !effectiveReadOnly && (
                                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                                        {situationSuggestions.map((s) => (
                                            <WordingGuardSuggestionChip
                                                key={`${situationKey}-${s.source}-${s.target}`}
                                                label={`${s.source} -> ${s.target} (${s.matches})`}
                                                onAccept={() =>
                                                    acceptSuggestion(situationKey, lang, "situation", situationValue, s.source, s.target, severity)
                                                }
                                                onDismiss={() => dismissSuggestion(situationKey, s.source, s.target)}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Grid>
                            <Grid size={{ xs: 1 }} sx={infoIconGridSxMultiline}>
                                <Tooltip title="System state when event occurred" arrow>
                                    <span style={{ display: "inline-flex" }}>
                                        <InfoOutlineIcon />
                                    </span>
                                </Tooltip>
                            </Grid>

                            {/* ACTIONS */}
                            {!isManufacturer && (
                                <Grid size={{ xs: 12 }}>
                                    <ActionStepsInput
                                        desc={desc}
                                        isReader={effectiveReadOnly}
                                        handleDescriptionsChange={onChange}
                                        getSuggestionsForAction={getActionSuggestions}
                                        onAcceptSuggestion={acceptActionSuggestion}
                                        onIgnoreSuggestion={ignoreActionSuggestion}
                                        suggestedTemplates={lang === "ger" && textSuggestionsEnabled ? actionTemplates : []}
                                    />
                                </Grid>
                            )}
                        </React.Fragment>
                    );
                })}
            </Grid>
        );
    }
);